import { UsageLimitEntitlementName } from '@jupiterone/entitlement-types';
import { AbacRuleName } from '@jupiterone/jupiter-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon';
import React from 'react';
import { ChevronDown, ChevronUp, Copy, TrendingUp } from 'react-feather';
import AlertsIcon from 'react-feather/dist/icons/bell';
import ViewIcon from 'react-feather/dist/icons/eye';
import ShareIcon from 'react-feather/dist/icons/share';
import StarIcon from 'react-feather/dist/icons/star';
import CloseIcon from 'react-feather/dist/icons/x';
import compact from '../../../util/compact';
import CopyToClipboard from '../../util/CopyToClipboard';

export interface ToolbarItemDescriptor {
  title: string;
  onClick?: (event: any) => void;
  icon: JSX.Element;
  entitlement?: UsageLimitEntitlementName;
  abac?: AbacRuleName[];
}

interface FlagWithHandler {
  enabled: boolean;
  onClick?: (event: any) => void;
  entitlement?: UsageLimitEntitlementName;
}

type OptionalFlagWithHandler = Partial<FlagWithHandler>;

interface QueryToolbarItemsInput {
  query: string;
  isExporting?: boolean;
  viewAs?: OptionalFlagWithHandler;
  copy?: OptionalFlagWithHandler;
  alert?: OptionalFlagWithHandler;
  star?: OptionalFlagWithHandler;
  remove?: OptionalFlagWithHandler;
  exportItem?: OptionalFlagWithHandler;
  expand?: OptionalFlagWithHandler;
  minimize?: OptionalFlagWithHandler;
  addToInsights?: OptionalFlagWithHandler;
}

type PartialItemDescriptor = Omit<ToolbarItemDescriptor, 'onClick'>;

const toFlagWithHandler = (
  options?: OptionalFlagWithHandler
): FlagWithHandler => {
  const defaults = { enabled: true };

  if (!options) {
    return defaults;
  }
  return { ...defaults, ...options };
};

const item = (
  options: OptionalFlagWithHandler | undefined,
  render: () => PartialItemDescriptor
) => {
  const { enabled, onClick, entitlement } = toFlagWithHandler(options);

  if (!enabled) {
    return null;
  }
  return { ...render(), onClick, entitlement };
};

const toolbarItemDescriptors = ({
  query,
  isExporting,
  viewAs,
  copy,
  alert,
  star,
  remove,
  exportItem,
  expand,
  minimize,
  addToInsights,
}: QueryToolbarItemsInput): ToolbarItemDescriptor[] =>
  compact([
    item(expand, () => ({
      title: 'Expand query',
      icon: <ChevronDown />,
    })),
    item(minimize, () => ({
      title: 'Collapse query',
      icon: <ChevronUp />,
    })),
    item(viewAs, () => ({
      title: 'View as...',
      icon: <ViewIcon />,
    })),

    item(exportItem, () => {
      if (isExporting) {
        return {
          title: 'Exporting...',
          icon: (
            <Icon>
              <CircularProgress variant="indeterminate" size={23} />
            </Icon>
          ),
        };
      } else {
        return {
          title: 'Export',
          icon: <ShareIcon />,
        };
      }
    }),

    item(copy, () => ({
      title: 'Copy query to clipboard',
      icon: (
        <CopyToClipboard text={query}>
          <Copy />
        </CopyToClipboard>
      ),
    })),

    item(addToInsights, () => ({
      title: 'Add to Insights',
      icon: <TrendingUp />,
      abac: ['accessAdmin', 'adminInsights'],
    })),

    item(alert, () => ({
      title: 'Create alert from query',
      icon: <AlertsIcon />,
      abac: ['accessAdmin', 'adminRules'],
    })),

    item(star, () => ({
      title: 'Save',
      icon: <StarIcon />,
    })),

    item(remove, () => ({
      title: 'Close',
      icon: <CloseIcon />,
    })),
  ]);

export default toolbarItemDescriptors;
